import { useState } from 'react';
import {
  useAgentCatalogs,
  useAttachAgentCatalog,
  useCatalogs,
  useDetachAgentCatalog,
  useSetAgentCatalogWritable,
} from '@/lib/api/catalogs';
import { useTenantContext } from '@/auth/TenantContext';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';
import { QueryState } from '@/components/QueryState';

interface AgentCatalogsPanelProps {
  agentId: string;
}

/** Assigned-catalogs selector for an agent: list, assign, unassign, and the
 * per-catalog write grant (spec 0017 AC-15). Assignments are read-only until
 * an owner/admin explicitly grants write access; the toggle is disabled for
 * members, matching the server-side rule. */
export function AgentCatalogsPanel({ agentId }: AgentCatalogsPanelProps) {
  const linked = useAgentCatalogs(agentId);
  const allCatalogs = useCatalogs();
  const attach = useAttachAgentCatalog(agentId);
  const detach = useDetachAgentCatalog(agentId);
  const setWritable = useSetAgentCatalogWritable(agentId);
  const { activeTenant } = useTenantContext();
  const canGrant = activeTenant?.role === 'owner' || activeTenant?.role === 'admin';
  const [selected, setSelected] = useState('');

  const linkedIds = new Set((linked.data ?? []).map((c) => c.id));
  const available = (allCatalogs.data ?? []).filter((c) => !linkedIds.has(c.id));

  function handleAttach() {
    if (!selected) return;
    attach.mutate(selected, { onSuccess: () => setSelected('') });
  }

  function handleToggleWritable(catalogId: string, name: string, writable: boolean) {
    if (writable) {
      // Granting write access opens this catalog to agent-driven mutation,
      // including from email and Slack runs -- confirm with context.
      const ok = window.confirm(
        `Allow this agent to create and update products in "${name}"? ` +
          'Writes apply immediately and are recorded in the change log.',
      );
      if (!ok) return;
    }
    setWritable.mutate({ catalogId, writable });
  }

  return (
    <Card
      title="Catalogs"
      description="Product catalogs this agent can search, and which ones it may write to."
    >
      <QueryState
        isLoading={linked.isLoading}
        error={linked.error}
        isEmpty={(linked.data ?? []).length === 0}
        emptyMessage="No catalogs assigned yet."
      >
        <ul className="mb-4 divide-y divide-gray-100">
          {linked.data?.map((catalog) => (
            <li
              key={catalog.id}
              className="flex items-center justify-between gap-3 py-2"
              data-testid="agent-linked-catalog"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-gray-900">{catalog.name}</p>
                <p className="truncate text-xs text-gray-500">{catalog.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={
                    catalog.writable
                      ? 'rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700'
                      : 'rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600'
                  }
                  data-testid="catalog-writable-state"
                >
                  {catalog.writable ? 'Writable' : 'Read-only'}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    handleToggleWritable(catalog.id, catalog.name, !catalog.writable)
                  }
                  disabled={setWritable.isPending || (!catalog.writable && !canGrant)}
                  title={
                    !catalog.writable && !canGrant
                      ? 'Only an owner or admin can grant write access'
                      : undefined
                  }
                  data-testid="catalog-writable-toggle"
                >
                  {catalog.writable ? 'Make read-only' : 'Allow writes'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => detach.mutate(catalog.id)}
                  disabled={detach.isPending}
                  data-testid="detach-catalog-button"
                >
                  Unassign
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </QueryState>

      {setWritable.error ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {setWritable.error instanceof Error
            ? setWritable.error.message
            : 'Could not change write access.'}
        </div>
      ) : null}

      {(linked.data ?? []).length > 0 && (
        <p className="mb-4 text-xs text-gray-500" data-testid="catalog-tools-note">
          Catalog tools (lookup, search, semantic search, filter discovery) are active for this
          agent. Writable catalogs additionally let it create and update products; every write
          is recorded in the change log.
        </p>
      )}

      {available.length > 0 && (
        <div className="flex items-end gap-2">
          <Select
            label="Assign a catalog"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="attach-catalog-select"
          >
            <option value="">Select a catalog...</option>
            {available.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="secondary"
            onClick={handleAttach}
            disabled={!selected || attach.isPending}
            data-testid="attach-catalog-button"
          >
            Assign
          </Button>
        </div>
      )}
    </Card>
  );
}
